<template lang="pug">
//- Time that was worked without the clock running: the call you took before
//- reaching for the timer, the fix you remembered on the way home. Reachable
//- whether or not a timer is running, which is why it does not live in the
//- start dialog — that one is out of reach exactly when a clock is going.
el-dialog.add-time-entry-dialog(
    :title="$t('Log time on {desk}', { desk: deskLabel })"
    :visible="visible"
    width="480px"
    append-to-body
    @update:visible="$emit('update:visible', $event)"
    @closed="reset"
)
    el-form(label-position="top" size="small" @submit.native.prevent)
        //- Where the time lands. Small in-between work needs no ticket in the
        //- list, so it opens one that stays off it.
        el-form-item(:label="$t('Book the time on')")
            el-radio-group(v-model="target" size="mini")
                el-radio-button(v-if="canCreateTickets" label="quick") {{ $t('Without a ticket') }}
                el-radio-button(label="ticket") {{ $t('An open ticket') }}

        template(v-if="target === 'quick'")
            el-form-item(:label="$t('What is it about?')")
                el-input(
                    ref="subjectInput"
                    v-model="subject"
                    :placeholder="isPlatform ? $t('Appears on the billing breakdown.') : $t('A short note on what it was.')"
                )
            el-form-item(v-if="isPlatform" :label="$t('Website')")
                el-select(
                    v-model="selectedCmsId"
                    style="width: 100%"
                    clearable
                    filterable
                    remote
                    :remote-method="searchCustomers"
                    :loading="loadingCustomers"
                    :placeholder="$t('No website')"
                )
                    el-option(v-for="cms in cmsOptions" :key="cms.id" :label="customerLabel(cms)" :value="cms.id")
                p.field-hint {{ $t('Without a website the time is our own and is not billed.') }}

        el-form-item(v-else :label="$t('Ticket')")
            el-select(
                v-model="ticketId"
                filterable
                style="width: 100%"
                :loading="loadingTickets"
                :placeholder="openTickets.length ? $t('Select ticket') : $t('No open tickets')"
                :no-data-text="$t('No open tickets')"
                popper-class="ticket-option-dropdown"
                @visible-change="onTicketDropdown"
            )
                el-option(
                    v-for="ticket in openTickets"
                    :key="ticket.id"
                    :label="ticket.number + ' · ' + ticketSubject(ticket.subject, $t('No subject'))"
                    :value="ticket.id"
                )
                    ticket-option(:ticket="ticket")

        //- The same three fields the ticket's own time panel asks for, so a
        //- booking made here reads like one made there.
        //- The two ends of the span side by side; the duration under them
        //- is the same span read a third way (see timeEntrySpan.js).
        el-row(:gutter="8")
            el-col(:span="12")
                el-form-item(:label="$t('Start')")
                    el-date-picker(v-model="startedAt" type="datetime" style="width: 100%" format="dd.MM.yyyy HH:mm" :picker-options="{ firstDayOfWeek: 1 }" @change="onStartChanged")
            el-col(:span="12")
                el-form-item(:label="$t('End')")
                    el-date-picker(v-model="endedAt" type="datetime" style="width: 100%" format="dd.MM.yyyy HH:mm" :picker-options="{ firstDayOfWeek: 1 }" @change="onEndChanged")
        el-form-item(:label="$t('Duration')")
            duration-input(v-model="durationMinutes" @input="onDurationChanged")
        el-form-item(:label="$t('Note')")
            el-input(v-model="note" type="textarea" :rows="2" :placeholder="isPlatform ? $t('What was done? Appears on the billing breakdown.') : $t('What was done?')")
        //- Only our own desk bills: the synchronizer counts `desk = platform`,
        //- so a shop's own switch would promise something that never happens.
        el-form-item(v-if="isPlatform")
            billable-state-select(v-model="billable")

    template(slot="footer")
        el-button(size="mini" icon="el-icon-fa-times fas" @click="$emit('update:visible', false)") {{ $t('Cancel') }}
        el-button(
            size="mini"
            type="primary"
            icon="el-icon-fa-check fas"
            :loading="saving"
            :disabled="!isValid"
            @click="save"
        ) {{ $t('Log time') }}
</template>

<i18n>
{
    "en": {
        "Log time on {desk}": "Log time on {desk}"
    },
    "de": {
        "Log time": "Zeit nachtragen",
        "Log time on {desk}": "Zeit nachtragen auf {desk}",
        "A short note on what it was.": "Kurz notieren, worum es ging.",
        "What was done?": "Was wurde gemacht?",
        "Book the time on": "Zeit buchen auf",
        "Without a ticket": "Ohne Ticket",
        "An open ticket": "Offenes Ticket",
        "What is it about?": "Worum geht es?",
        "Appears on the billing breakdown.": "Erscheint in der Abrechnungsübersicht.",
        "Platform": "Plattform",
        "Website": "Webauftritt",
        "No website": "Kein Webauftritt",
        "Without a website the time is our own and is not billed.": "Ohne Webauftritt ist die Zeit unsere eigene und wird nicht berechnet.",
        "Ticket": "Ticket",
        "Select ticket": "Ticket auswählen",
        "No open tickets": "Keine offenen Tickets",
        "No subject": "Ohne Betreff",
        "Start": "Beginn",
        "End": "Ende",
        "Duration": "Dauer",
        "Note": "Notiz",
        "What was done? Appears on the billing breakdown.": "Was wurde gemacht? Erscheint in der Abrechnungsübersicht.",
        "Cancel": "Abbrechen",
        "Time logged": "Zeit nachgetragen",
        "An error occurred!": "Ein Fehler ist aufgetreten!",
        "The time could not be logged.": "Die Zeit konnte nicht nachgetragen werden."
    }
}
</i18n>

<script>
/**
 * Booking time that is already over. Two destinations: a ticket that is open
 * anyway, or none at all — which opens a quick task, a real billable ticket
 * that stays off the list.
 *
 * The quick-task path is two calls (create the ticket, then the entry) because
 * both mutations exist and are tested. If the second fails, what is left is a
 * named ticket with no time on it, sitting on the Kleinaufgaben shelf where it
 * can be seen and dealt with — not a silent loss.
 */
import _ from 'lodash';

import { ticketSubject } from '@/filters';
import TicketApi from '@/extensions/tickets/store/api/ticket';
import { customerLabel } from '@/extensions/tickets/customerLabel';

import { useSetupStore, useTicketStore } from '@/stores';
import { checkRestrictions } from '@/helpers';
import DurationInput from '@/components/DurationInput';
import BillableStateSelect from '@/extensions/tickets/components/BillableStateSelect';
import { endFor, minutesBetween, spanAfterStartChange } from '@/extensions/tickets/timeEntrySpan';
import TicketOption from '@/extensions/tickets/components/TicketOption';
import { TICKET_OPEN_STATUS_KEYS } from '@/extensions/tickets/ticketStatus';

export default {
    name: 'AddTimeEntryDialog',
    components: {
        'ticket-option': TicketOption,
        'duration-input': DurationInput,
        'billable-state-select': BillableStateSelect,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        // Which desk the time belongs to — the pill this dialog hangs off.
        desk: {
            type: String,
            required: true,
        },
        // The shop a tenant booking belongs to.
        cmsId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            target: 'quick',
            subject: '',
            selectedCmsId: null,
            cmsOptions: [],
            loadingCustomers: false,
            ticketId: null,
            openTickets: [],
            loadingTickets: false,
            startedAt: null,
            endedAt: null,
            durationMinutes: 15,
            note: '',
            billable: true,
            saving: false,
        };
    },
    computed: {
        canCreateTickets() {
            return checkRestrictions(['ex:tckt:tickets'], 'create');
        },
        isPlatform() {
            return this.desk === 'platform';
        },
        // Which of the two this is, in one word — two pills in the header book
        // on two different desks, and an unlabelled dialog says neither.
        deskLabel() {
            if (this.isPlatform) return this.$t('Platform');

            return _.get(useSetupStore(), 'activeCms.name') || this.$t('Website');
        },
        // Our own bookings ride our own token even from inside a customer's
        // account; a shop's own ride the account's.
        isValid() {
            if (this.durationMinutes <= 0) return false;

            return this.target === 'ticket' ? !!this.ticketId : true;
        },
    },
    watch: {
        visible(visible) {
            if (!visible) return;

            // A quick task opens a ticket, the create flag; without it the
            // time goes onto one that already exists.
            this.target = this.canCreateTickets ? 'quick' : 'ticket';
            this.subject = '';
            this.selectedCmsId = this.isPlatform ? null : this.cmsId;
            this.cmsOptions = [];
            this.ticketId = null;
            this.openTickets = [];
            this.startedAt = new Date();
            this.durationMinutes = 15;
            this.endedAt = endFor(this.startedAt, this.durationMinutes);
            this.note = '';
            this.billable = true;

            this.$nextTick(() => {
                const input = this.$refs.subjectInput;
                if (input) input.focus();
            });
        },
    },
    methods: {
        customerLabel,
        ticketSubject,
        // Start, End and Duration follow each other the way Toggl's do; see
        // timeEntrySpan.js. Handlers rather than watchers, so a value set by
        // one of them does not fire the next.
        onStartChanged(start) {
            if (!start) return;

            const { end, minutes } = spanAfterStartChange({ start, end: this.endedAt, minutes: this.durationMinutes });
            this.endedAt = end;
            this.durationMinutes = minutes;
        },
        onEndChanged(end) {
            if (!end) return;

            this.durationMinutes = minutesBetween(this.startedAt, end);
        },
        onDurationChanged(minutes) {
            this.endedAt = endFor(this.startedAt, minutes);
        },
        reset() {
            this.openTickets = [];
            this.cmsOptions = [];
        },
        searchCustomers(term) {
            if (!term) return;

            this.loadingCustomers = true;
            TicketApi.searchCms(term)
                .then((items) => {
                    this.cmsOptions = items;
                })
                .finally(() => {
                    this.loadingCustomers = false;
                });
        },
        onTicketDropdown(open) {
            if (!open || this.openTickets.length || this.loadingTickets) return;

            this.loadingTickets = true;
            const filters = [{ key: 'status', in: TICKET_OPEN_STATUS_KEYS }];
            const cms = this.isPlatform ? null : this.cmsId;
            if (cms) filters.push({ key: 'cms', match: cms });

            TicketApi.fetchPaginated({
                currentPage: 1,
                perPage: 50,
                order: 'created_at',
                direction: 'desc',
                link: '',
                desk: this.desk,
                filters,
            })
                .then((data) => {
                    this.openTickets = data.items || [];
                })
                .catch(() => {
                    this.openTickets = [];
                })
                .finally(() => {
                    this.loadingTickets = false;
                });
        },
        async save() {
            this.saving = true;
            try {
                const ticketId = this.target === 'ticket' ? this.ticketId : await this.openQuickTask();

                await TicketApi.saveTimeEntry(
                    {
                        entry: {
                            ticket: { id: ticketId },
                            started_at: this.toDateTimeString(this.startedAt),
                            duration_seconds: this.durationMinutes * 60,
                            note: this.note.trim() || null,
                            billable: this.billable,
                        },
                    }
                );

                this.$emit('update:visible', false);
                // The ticket's own time list and the running-entries drawer are
                // showing this desk; tell them.
                useTicketStore().sessionChanged();
                this.notify(this.$t('Time logged'));
            } catch (error) {
                /* eslint no-console: ["error", { allow: ["error"] }] */
                console.error(error);
                this.notify(_.get(error, 'message') || this.$t('The time could not be logged.'), 'error');
            } finally {
                this.saving = false;
            }
        },
        // The ticket a quick task needs in order to exist at all: it carries the
        // customer, the desk and the billing.
        async openQuickTask() {
            const setup = useSetupStore();
            const cms = this.isPlatform ? this.selectedCmsId : this.cmsId || _.get(setup, 'activeCms.id');

            const ticket = await TicketApi.create(
                {
                    cmsId: cms,
                    ticket: {
                        subject: this.subject.trim() || null,
                        desk: this.desk,
                        quick_task: true,
                        status: 'completed',
                    },
                }
            );

            return ticket.id;
        },
        // The same wire format the ticket's own time panel sends.
        toDateTimeString(value) {
            if (!value) return null;

            const date = value instanceof Date ? value : new Date(value);
            const pad = (part) => String(part).padStart(2, '0');

            return (
                `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
                ` ${pad(date.getHours())}:${pad(date.getMinutes())}:00`
            );
        },
        notify(message, type = 'success') {
            this.$notify[type]({
                title: message,
                iconClass: type === 'error' ? 'fas el-icon-fa-xmark' : 'fas el-icon-fa-check',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
    },
};
</script>

<style lang="sass" scoped>
.field-hint
    margin: 0.25rem 0 0
    font-size: 0.75rem
    line-height: 1.4
    color: #909399
</style>
